import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  useTheme,
  Chip,
  Stack,
} from '@mui/material';
import {
  School,
  MenuBook,
  Quiz,
  Construction,
  VideoLibrary,
  Assessment,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import FeatureGuard from '../components/FeatureGuard';

const MotionCard = motion(Card);

const EducationPlaceholderPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <School 
                sx={{ 
                  fontSize: '4rem',
                  color: theme.palette.primary.main,
                  filter: 'drop-shadow(0 4px 12px rgba(255, 215, 0, 0.3))'
                }} 
              />
            </Box>
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom 
              sx={{ 
                mb: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, #FFA726)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: 700,
              }}
            >
              Forex Education Center
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}
            >
              Comprehensive trading courses, tutorials, and educational resources for all skill levels
            </Typography>
          </motion.div>
        </Box>

        {/* Coming Soon Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <MotionCard
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.background.paper}, rgba(255, 215, 0, 0.02))`,
              border: `1px solid ${theme.palette.primary.main}`,
              borderRadius: 3,
              maxWidth: '800px',
              mx: 'auto',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
            whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)' }}
            transition={{ duration: 0.3 }}
          >
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Construction 
                  sx={{ 
                    fontSize: '3rem',
                    color: theme.palette.warning.main,
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.7 },
                      '100%': { opacity: 1 },
                    },
                  }} 
                />
              </Box>
              
              <Chip
                label="Coming Soon"
                color="warning"
                variant="outlined"
                sx={{
                  mb: 3,
                  px: 2,
                  py: 1,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderWidth: 2,
                }}
              />

              <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  mb: 3,
                }}
              >
                Feature Under Development
              </Typography>

              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ mb: 4, lineHeight: 1.8 }}
              >
                We're building a comprehensive education platform that will provide you with:
              </Typography>

              <Stack spacing={2} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <VideoLibrary sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body2" color="text.secondary">
                    Interactive video tutorials and courses
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <MenuBook sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body2" color="text.secondary">
                    Structured learning paths from beginner to advanced
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <Quiz sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body2" color="text.secondary">
                    Interactive quizzes and knowledge assessments
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <Assessment sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body2" color="text.secondary">
                    Trading simulators and practice tools
                  </Typography>
                </Box>
              </Stack>

              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{
                  fontStyle: 'italic',
                  p: 3,
                  backgroundColor: 'rgba(255, 215, 0, 0.05)',
                  borderRadius: 2,
                  border: `1px solid rgba(255, 215, 0, 0.2)`,
                }}
              >
                Our comprehensive education center is currently under development. 
                Get ready for the most advanced forex learning experience!
              </Typography>
            </CardContent>
          </MotionCard>
        </motion.div>
      </Container>
    </Box>
  );
};

function EducationPlaceholderFeature() {
  return (
    <FeatureGuard featureId="education">
      <EducationPlaceholderPage />
    </FeatureGuard>
  );
}

export const EducationPlaceholder = EducationPlaceholderFeature;

export default EducationPlaceholderFeature; 